import { Link } from "react-router-dom";
import { ArrowRight, Beer, CalendarDays, Sparkles, Users } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/club";

const SECTIONS = [
  {
    icon: Beer,
    title: "The Pavilion Bar",
    text: "The bar at West Park is open on matchdays and through the week in season — cold drinks, good company and the best view of the cricket in Whitley Bay.",
  },
  {
    icon: CalendarDays,
    title: "Summer Events",
    text: "BBQs on the bank, presentation nights, quiz evenings and end-of-season celebrations. The social calendar runs as hard as the fixture list.",
  },
  {
    icon: Sparkles,
    title: "All Stars Evenings",
    text: "When ECB All Stars takes over the outfield in summer, the pavilion opens for parents — cricket for the kids, a drink and a chat for the grown-ups.",
  },
  {
    icon: Users,
    title: "Social Membership",
    text: "You don't need to play to belong. Social members keep the club buzzing — supporters, families and friends of West Park are always welcome.",
  },
];

export default function Social() {
  return (
    <main data-testid="social-page">
      <Seo
        title="Social & Bar — Whitley Bay Cricket Club"
        description="The social side of Whitley Bay CC: the pavilion bar at West Park, summer events, All Stars evenings and social membership."
      />
      <PageHeader
        eyebrow="Social & bar"
        title="Good cricket."
        accent="Great company."
        description="West Park is as much a social club as a cricket one. Pull up a chair — you're very welcome."
        image={IMAGES.clubhouse}
        testId="social-header"
      />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 grid gap-6 sm:grid-cols-2">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={(i % 2) * 0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#122030] p-8 hover:border-[#E5AC3C]/40 transition-colors" data-testid={`social-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <s.icon size={26} className="text-[#E5AC3C]" />
              <h2 className="mt-4 font-head text-xl font-bold">{s.title}</h2>
              <p className="mt-3 text-sm sm:text-base text-[#9BB0C1] leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="pavilion-inside">
        <Reveal>
          <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">Inside the pavilion</p>
          <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mb-8">
            New bar, new decking, <span className="font-serif-accent font-medium text-[#E5AC3C]">five-star kitchen</span>
          </h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="pavilion-lounge-photo">
              <img src="/assets/clubhouse-lounge.webp" alt="The refurbished clubhouse lounge at West Park, set up for events" className="w-full h-64 object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.04}>
            <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="pavilion-bar-photo">
              <img src="/assets/bar-interior.webp" alt="The newly refurbished bar inside the West Park pavilion" className="w-full h-64 object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="pavilion-buffet-photo">
              <img src="/assets/clubhouse-buffet.jpg" alt="Matchday hospitality spread in the clubhouse" className="w-full h-64 object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white p-6 flex flex-col items-center justify-center gap-3 text-center" data-testid="hygiene-rating-badge">
              <img src="/assets/hygiene-rating.jpg" alt="Food Standards Agency hygiene rating: 5, very good" className="max-h-32 w-auto" loading="lazy" />
              <span className="text-xs font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">Rated 5 — Very Good · Food Standards Agency</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10">
            <img src="/assets/decking.webp" alt="The new decking overlooking the ground at West Park" className="w-full h-[320px] sm:h-[420px] object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1520] via-[#0B1520]/40 to-transparent" aria-hidden="true" />
            <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
              <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight max-w-lg">
                Hire the clubhouse for <span className="font-serif-accent font-medium text-[#E5AC3C]">your event</span>
              </h2>
              <p className="mt-3 text-[#9BB0C1] max-w-md text-sm sm:text-base">
                Birthdays, celebrations, meetings — the pavilion at West Park is available for private hire.
              </p>
              <Link to="/booking" data-testid="social-hire-cta" className="mt-6 inline-flex items-center gap-2 bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
                Enquire about hiring <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
